//import { sessionStorageReducer } from './state/reducers/session-storage-reducer';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, TitleStrategy } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  MatomoInitializationMode,
  NgxMatomoTrackerModule,
} from '@ngx-matomo/tracker';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { authInterceptorProvider } from '@services/authinterceptor.service';
import { jwtInterceptorProvider } from '@services/jwt.interceptor';
import { signalRConnectionInterceptorProvider } from '@services/signalr-connection.interceptor';
import { TokenRefreshService } from '@services/token-refresh.service';
import { TokenService } from '@services/token.service';
import { ExtendedTranslateService } from '@services/extended-translate.service';
import { AppLanguageService } from '@services/app-language.service';
import { CatalogTranslateLoader } from '@services/catalog-translate-loader';
import { LogoutMetaReducer } from '@state/reducers/logout.reducer';
import { LoadingInterceptor } from '@utils/loading-interceptor';
import { ThemeRouter } from '@utils/theme-router';
import { AppTitleStrategy } from '@utils/app-title-strategy';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CookieService } from 'ngx-cookie-service';
import { MarkdownModule } from 'ngx-markdown';
import { firstValueFrom, switchMap } from 'rxjs';
import { environment } from '../environments/environment';
import { ROUTES } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthShellComponent } from './auth-shell.component';
import { CsoBreadcrumbTooltipComponent } from './components/cso-breadcrumb-tooltip/cso-breadcrumb-tooltip.component';
import { FilterProfileCreateNewDialogComponent } from './components/filter-profile-create-new-dialog/filter-profile-create-new-dialog.component';
import { GlobalTankSearchResultComponent } from './components/global-tank-search/global-tank-search-results.component';
import { GlobalTankSearchComponent } from './components/global-tank-search/global-tank-search.component';
import { LeftNavSecondaryLinksComponent } from './components/left-nav-secondary-links/left-nav-secondary-links.component';
import { OrganizationPopupComponent } from './components/organization-popup/organization-popup.component';
import { TankProfileCreateNewDialogComponent } from './components/tank-profile-create-new-dialog/tank-profile-create-new-dialog.component';
import { DeleteFailureModalComponent } from '@components/delete-failure-modal/delete-failure-modal.component';
import { FooterComponent } from './footer/footer.component';
import { DirtyModelGuard } from './guards/dirtyModel.guard';
import { HasManageOrganizationsOrPremiumServicesGuard } from './guards/hasManageOrganizationsOrPremiumServices.guard';
import { HasWebAccessGuard } from './guards/hasWebAccess.guard';
import { LoggedInGuard } from './guards/loggedin.guard';
import { SecurityPolicyGuard } from './guards/security-policy.guard';
import { UserDetailsGuard } from './guards/user-details.guard';
import { ChatComponent } from './header/chat/chat.component';
import { HeaderComponent } from './header/header.component';
import { ConfirmationDialogModule } from './modules/confirmation-dialog/confirmation-dialog.module';
import { TankQuickFiltersModule } from './modules/tank-quick-filters/tank-quick-filters.module';
import { ThemingModule } from './modules/theming/theming.module';
import { LoginComponent } from './page/login/login.component';
import { EmailVerificationStepComponent } from './page/login/email-verification-step/email-verification-step.component';
import { SecondFactorStepComponent } from './page/login/second-factor-step/second-factor-step.component';
import { ForgotPasswordDialogComponent } from './page/login/forgot-password-dialog/forgot-password-dialog.component';
import { PublicPageLayoutComponent } from './shared/components/public-page-layout/public-page-layout.component';
import { DeviceService } from './services/device.service';
import { EventEmiterService } from './services/event.emmiter.service';
import { FilterProfileService } from './services/filter-profile.service';
import { GridService } from './services/grid.service';
import { LocationService } from './services/location.service';
import { LogService } from './services/log.service';
import { OrganizationService } from './services/organization.service';
import { ResetSaveButtonService } from './services/reset-save-button.service';
import { TankProfileService } from './services/tank-profile.service';
import { TankViewProfileService } from './services/tank-view-profile.service';
import { ThemingService } from './services/theming.service';
import { UsageService } from './services/usage.service';
import { UserService } from './services/user.service';
import { SharedModule } from './shared/modules/shared.module';
import { TankProfileEffect } from './state/effects/tank-profile.effect';
import {
  filterReducer,
  gridFilterExtensionReducer,
  gridFilterReducer,
  usageReducer,
  userProfileReducer,
} from './state/reducers';
import { filteredTanksReducer, filterProfilesReducer } from './state/reducers/filter.reducer';
import { tankNotificationsReducer } from './state/reducers/tank-notifications.reducer';
import { currentTankProfileReducer } from './state/reducers/tank-profile.reducer';
import { tankManagementReducer } from './state/reducers/tankmanagement.reducer';
import { TapAppStore } from './state/tap-state';
import { BoolToYesNo } from './utilities/boolToYesNo.pipe';
import CommonMissingTranslationHandler from './utilities/common-missing-translation-handler';
import { SettingsModule } from '@components/settings/settings.module';
import { ExportToExcelService } from './services/export-to-excel.service';
import { UserInformationEffect } from './state/effects/user-information.effect';
import { ManageLocationDialogComponent } from './page/organization/components/manage-location-dialog/manage-location-dialog.component';
import { LocationPreviewDialogComponent } from './components/import-locations-dialog/location-preview-dialog.component';
import { BulkImportResultDialogComponent } from './components/bulk-import-result-dialog/bulk-import-result-dialog.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ProcessingDialogComponent } from './components/processing-dialog/processing-dialog.component';
import {UserPrivilegesEventEmitterService } from './services/user-privileges-event.emitter.service'
import { MoveTankService } from './modules/tank-move/services/move-tank.service';
import { BulkTankImportResultDialogComponent } from './page/organization/components/organization-tank-setup-grid/bulk-tank-import-result-dialog.component';
import { BulkTankImportProgressDialogComponent } from './page/organization/components/organization-tank-setup-grid/bulk-tank-import-progress-dialog.component';
import { CustomTemplateDialogComponent } from './page/organization/components/organization-tank-setup-grid/custom-template-dialog.component';

/**
 * Recovers an access token, then loads the strings in that user's language, before the app runs.
 *
 * TokenService holds the token in memory, so a reload starts with nothing and every request would
 * go out unauthenticated. This spends the HttpOnly refresh cookie once, up front, so components and
 * guards can assume a token exists exactly as they did when it lived in localStorage.
 *
 * The two steps are one initializer because their order matters: Angular runs initializers in
 * parallel, and the strings used to be fetched alongside the refresh rather than after it, so they
 * could go out before the token existed and come back in en-US whatever the user's language.
 *
 * It resolves either way and never rejects. There is no token to recover on the login page, and a
 * rejected APP_INITIALIZER stops the app bootstrapping at all - a failed refresh has to mean "you
 * are not signed in", and failed strings mean [KEY] placeholders, never "the application did not
 * start". Neither refresh() nor AppLanguageService.use() errors.
 */
export function initApp(
  tokenRefresh: TokenRefreshService,
  tokenService: TokenService,
  language: AppLanguageService,
) {
  return () =>
    firstValueFrom(
      tokenRefresh
        .refresh()
        .pipe(switchMap(() => language.use(tokenService.getCultureCode()))),
    );
}

@NgModule({
  declarations: [
    AppComponent,
    BoolToYesNo,
    FooterComponent,
    HeaderComponent,
    OrganizationPopupComponent,
    CsoBreadcrumbTooltipComponent,
    TankProfileCreateNewDialogComponent,
    FilterProfileCreateNewDialogComponent,
    GlobalTankSearchComponent,
    GlobalTankSearchResultComponent,
    DeleteFailureModalComponent,
    LeftNavSecondaryLinksComponent,
    LoginComponent,
    EmailVerificationStepComponent,
    ForgotPasswordDialogComponent,
    AuthShellComponent,
    ChatComponent,
    ManageLocationDialogComponent,
    LocationPreviewDialogComponent,
    BulkImportResultDialogComponent,
    ProcessingDialogComponent,
    BulkTankImportResultDialogComponent,
    BulkTankImportProgressDialogComponent,
    CustomTemplateDialogComponent
  ],
  imports: [
    SecondFactorStepComponent,
    MarkdownModule,
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.MANUAL,
    }),
    RouterModule.forRoot(ROUTES, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'disabled',
      anchorScrolling: 'enabled',
      // enableTracing: true,
    }),
    StoreModule.forRoot(
      {
        currentFilterProfile: filterReducer,
        gridFilter: gridFilterReducer,
        gridFilterExtensions: gridFilterExtensionReducer,
        usage: usageReducer,
        userInformation: userProfileReducer,
        currentTankProfile: currentTankProfileReducer,
        filteredTanks: filteredTanksReducer,
        currentTank: tankManagementReducer,
        currentTankNotifications: tankNotificationsReducer,
        filterProfiles: filterProfilesReducer,
      },
      {
        metaReducers: [/* sessionStorageReducer, */ LogoutMetaReducer],
      },
    ),
    EffectsModule.forRoot([TankProfileEffect, UserInformationEffect]),
    OAuthModule.forRoot(),
    AngularSvgIconModule.forRoot(),
    SharedModule,
    PublicPageLayoutComponent,
    ThemingModule,
    BrowserModule,
    BrowserAnimationsModule,
    ConfirmationDialogModule,
    SettingsModule,
    TankQuickFiltersModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useClass: CatalogTranslateLoader },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CommonMissingTranslationHandler,
      },
    }),
    MatProgressBarModule,
  ],
  providers: [
    MoveTankService,
    { provide: TranslateService, useClass: ExtendedTranslateService },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [TokenRefreshService, TokenService, AppLanguageService],
      multi: true,
    },
    { provide: 'LoggedInGuard', useValue: LoggedInGuard },
    SecurityPolicyGuard,
    UserDetailsGuard,
    HasWebAccessGuard,
    DirtyModelGuard,
    HasManageOrganizationsOrPremiumServicesGuard,
    CookieService,
    authInterceptorProvider,
    jwtInterceptorProvider,
    signalRConnectionInterceptorProvider,
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    {
      provide: 'DS_API',
      useValue: `${environment.baseUrl}${environment.apiUrl}`,
    },
    { provide: OAuthStorage, useValue: sessionStorage },
    { provide: Router, useClass: ThemeRouter },
    { provide: TitleStrategy, useClass: AppTitleStrategy },

    EventEmiterService,
    OrganizationService,
    UserService,
    TankViewProfileService,
    TankProfileService,
    LogService,
    FilterProfileService,
    ThemingService,
    DeviceService,
    UsageService,
    LocationService,
    GridService,
    TapAppStore,
    ResetSaveButtonService,
    ExportToExcelService,
    UserPrivilegesEventEmitterService,
    ...(isDevMode() ? [provideStoreDevtools({
      maxAge: 25,
      autoPause: true,
      traceLimit: 75,
    })] : []),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(protected translate: TranslateService) {
    //
  }
}
